import { FC, useCallback, useEffect, useState } from 'react';
import { useFormContext, Controller, get } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import styled from 'styled-components';
import { Colors, FontSize, FontWeight, Spacing } from '../../../assets/GlobalStyle/Variables';
import { components } from 'react-select';
import { ValidationRule } from './validate';

export type AsyncOption = {
  value: number;
  label: string;
  description?: string;
};

type AsyncControlSelectProps = {
  name: string;
  id: string;
  label: string;
  loadOptions: (inputValue: string) => Promise<AsyncOption[]>;
  loadInitialOption?: (id: number) => Promise<AsyncOption | null>;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  validate?: ValidationRule[]
};

type InnerProps = {
  field: { value: any; onChange: (val: number | null) => void; onBlur: () => void; name: string; ref: any };
  hasError: boolean;
  error?: any;
  loadOptions: AsyncControlSelectProps['loadOptions'];
  loadInitialOption?: AsyncControlSelectProps['loadInitialOption'];
  disabled: boolean;
  placeholder: string;
};

const AsyncSelectInner: FC<InnerProps> = ({ field, hasError, error, loadOptions, loadInitialOption, disabled, placeholder }) => {
  const [selectedOption, setSelectedOption] = useState<AsyncOption | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = field.value;
    if (id && typeof id === 'number' && id !== 0) {
      if (loadInitialOption) {
        setLoading(true);
        loadInitialOption(id).then((opt) => {
          setSelectedOption(opt);
          setLoading(false);
        });
      }
    } else {
      setSelectedOption(null);
    }
  }, [field.value]);

  const selectStyles = {
    control: (base: any, { isDisabled }: { isDisabled: boolean }) => ({
      ...base,
      borderRadius: 0,
      height: 42,
      borderStyle: 'none',
      boxShadow: 'none',
      backgroundColor: isDisabled ? Colors.disabled : 'transparent',
      '&:hover': {
        borderColor: hasError ? Colors.error : Colors.disabledHover
      }
    }),
    option: (base: any) => ({
      ...base,
      padding: `${Spacing(2)} ${Spacing(6)} 0`,
      margin: 0
    })
  };

  const Option = (props: any) => {
    return (
      <components.Option {...props}>
        <StyledOptionWrapper>
          <StyledOptionLabel>{props.data.label}</StyledOptionLabel>
          {props.data.description && (
            <StyledOptionDescription>{props.data.description}</StyledOptionDescription>
          )}
        </StyledOptionWrapper>
      </components.Option>
    );
  };

  return (
    <StyledSelectWrapper>
      <StyledAsyncSelect
        $hasError={hasError}
        inputId={field.name}
        cacheOptions
        loadOptions={loadOptions}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(val, meta) => {
          if (meta.action === 'input-change') setInputValue(val);
          if (meta.action === 'menu-close') return inputValue;
          return val;
        }}
        onChange={(option) => {
          const opt = option as AsyncOption | null;
          setSelectedOption(opt);
          field.onChange(opt ? opt.value : null);
          setInputValue('');
        }}
        onBlur={field.onBlur}
        name={field.name}
        ref={field.ref}
        isDisabled={disabled}
        isClearable
        placeholder={loading ? '' : placeholder}
        styles={selectStyles}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2 ? 'Začněte psát pro vyhledání...' : 'Žádné výsledky'
        }
        loadingMessage={() => 'Načítání...'}
        components={{ Option }}
      />
      {error?.message && <span role="alert">{String(error.message)}</span>}
    </StyledSelectWrapper>
  );
};

export const AsyncControlSelect: FC<AsyncControlSelectProps> = (props) => {
  const {
    name,
    id,
    label,
    loadOptions,
    loadInitialOption,
    disabled = false,
    required = false,
    placeholder = 'Hledat...',
    validate
  } = props;

  const methods = useFormContext();

  const validators = validate ?? [];

  const validation = useCallback(async (value: unknown) => {
    const formValues = methods.getValues() as Record<string, unknown>;

    for (const rule of validators) {
      const result = await rule(value, formValues);
      if (result !== true) {
        return result;
      }
    }

    return true;
  }, [validators, methods]);

  const hasError = !!get(methods.formState.errors, name);
  const error = get(methods.formState.errors, name);

  return (
    <Wrapper>
      {label && (
        <StyledLabel htmlFor={id}>
          {label}:{required && ' *'}
        </StyledLabel>
      )}
      <Controller
        name={name}
        control={methods.control}
        rules={{ required: required && 'Povinné pole', validate: validation }}
        render={({ field }) => (
          <AsyncSelectInner
            field={field}
            hasError={hasError}
            error={error}
            loadOptions={loadOptions}
            loadInitialOption={loadInitialOption}
            disabled={disabled}
            placeholder={placeholder}
          />
        )}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  margin-bottom: ${Spacing(8)};
`;

const StyledSelectWrapper = styled.div`
  width: 100%;

  span[role='alert'] {
    display: block;
    margin: 2px 0px 10px 4px;
    color: ${Colors.error};
    font-size: ${FontSize.small};
  }
`;

const StyledAsyncSelect = styled(AsyncSelect)<{ $hasError?: boolean }>`
  margin: 0px;
  border: 1px solid ${(props) => (props.$hasError ? Colors.error : props.isDisabled ? 'transparent' : Colors.disabledHover)};
  line-height: 30px;
  height: 42px;
  box-sizing: border-box;
  width: 100%;
  border-radius: 0px;
  color: ${Colors.grey};
  font-size: ${FontSize.normal};
  background-color: ${Colors.white};

  &:focus {
    outline: none;
    border: 1px solid ${(props) => (props.$hasError ? Colors.error : props.isDisabled ? 'transparent' : Colors.disabledHover)};
  }
`;

const StyledLabel = styled.label`
  margin: 0;
  color: ${Colors.grey};
  font-weight: ${FontWeight.strong};
  font-size: ${FontSize.normal};
  white-space: nowrap;
  min-width: 200px;
`;

const StyledOptionWrapper = styled.label`
  display: flex;
  flex-flow: column;
`;

const StyledOptionLabel = styled.span`
  font-size: ${FontSize.normal};
`;

const StyledOptionDescription = styled.span`
  font-size: ${FontSize.medium};
`;
